import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'; 
import { utils as XLSXUtils, writeFile as XLSXWriteFile } from 'xlsx';
import { db } from '../../firebase';
import logoImg from '../../components/image/PrrepX Logo.jpg';
import styles from './AdminInvoices.module.css';


const AdminInvoices = () => {
    const { userData } = require('../../context/AuthContext').useAuth();
    // All hooks must be called before any return!
    const [invoices, setInvoices] = useState([]);
    const [userMap, setUserMap] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [dateRange, setDateRange] = useState('all');
    const [loading, setLoading] = useState(true);
    const [downloadingInvoice, setDownloadingInvoice] = useState(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const isSuperadmin = userData?.role === 'superadmin';
    const canView = isSuperadmin || userData?.dashboardAccess?.invoices;

    useEffect(() => {
        const fetchInvoices = async () => {
            try {
                // Fetch ALL docs — orderBy excludes docs missing the 'date' field
                const querySnapshot = await getDocs(collection(db, "transactions"));
                const txs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                // Sort client-side: newest first, docs without date go to bottom
                txs.sort((a, b) => {
                    const aTime = a.date?.toDate?.() ?? a.timestamp?.toDate?.() ?? new Date(0);
                    const bTime = b.date?.toDate?.() ?? b.timestamp?.toDate?.() ?? new Date(0);
                    return bTime - aTime;
                });
                setInvoices(txs);

                const uids = Array.from(new Set(txs.map(tx => tx.userId).filter(Boolean)));
                const userMapTemp = {};
                await Promise.all(uids.map(async (uid) => {
                    try {
                        const userDoc = await getDoc(doc(db, 'users', uid));
                        if (userDoc.exists()) {
                            const data = userDoc.data();
                            userMapTemp[uid] = data.username || data.displayName || data.name || 'Student';
                        }
                    } catch (e) { console.error(e); }
                }));
                setUserMap(userMapTemp);
            } catch (error) {
                console.error("Error fetching invoices:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchInvoices();
    }, []);

    // Reset to page 1 when filters or per-page changes
    useEffect(() => { setPage(1); }, [searchTerm, dateRange, perPage]);

    if (!canView) {
        return <p className={styles.accessDenied}>🚨 Access Denied. You do not have permission to view this page.</p>;
    }

    const getDateLimit = () => {
        const now = new Date();
        if (dateRange === 'all') return null;
        const limits = { '1m': 1, '3m': 3, '6m': 6, '1y': 12, '2y': 24 };
        const d = new Date();
        d.setMonth(d.getMonth() - (limits[dateRange] || 1));
        return d;
    };

    const filtered = invoices.filter(inv => {
        const limit = getDateLimit();
        const studentName = userMap[inv.userId] || '';
        const matchesSearch = !searchTerm || 
            inv.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) || 
            inv.courseTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inv.courseId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            studentName.toLowerCase().includes(searchTerm.toLowerCase());
        // If doc has no date field, always show it (don't hide due to missing date)
        const invDate = inv.date?.toDate?.() ?? inv.timestamp?.toDate?.() ?? null;
        const matchesDate = !limit || !invDate || invDate >= limit;
        return matchesSearch && matchesDate;
    });

    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const safePage = Math.min(page, totalPages);
    const paginated = filtered.slice((safePage - 1) * perPage, safePage * perPage);

    // --- EXPORT TO EXCEL ---
    const handleExportExcel = () => {
        const data = filtered.map(inv => ({
            Date: inv.date?.toDate ? inv.date.toDate().toLocaleDateString() : 'N/A',
            Student: userMap[inv.userId] || 'N/A',
            Email: inv.userEmail,
            Course: inv.courseTitle,
            Amount: inv.amount,
            PaymentID: inv.paymentId
        }));
        const ws = XLSXUtils.json_to_sheet(data);
        const wb = XLSXUtils.book_new();
        XLSXUtils.book_append_sheet(wb, ws, 'Sales_Report');
        XLSXWriteFile(wb, `PrrepX_Sales_Report_${new Date().toLocaleDateString()}.xlsx`);
    };

    // --- EXPORT SUMMARY PDF (REPORT) ---
    const handleExportSummaryPDF = () => {
        const doc = new jsPDF('l', 'mm', 'a4'); // Landscape for better table space
        doc.setFontSize(20);
        doc.setTextColor(30, 58, 138);
        doc.text('Sales & Transactions Report', 14, 20);
        
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 28);
        doc.text(`Total Records: ${filtered.length}`, 14, 33);

        const tableColumn = ["Date", "Student", "Email", "Course/Test Series", "Amount", "Payment ID"];
        const tableRows = filtered.map(inv => [
            inv.date?.toDate ? inv.date.toDate().toLocaleDateString() : 'N/A',
            userMap[inv.userId] || 'N/A',
            inv.userEmail || 'N/A',
            inv.courseTitle || 'N/A',
            `Rs. ${inv.amount}`,
            inv.paymentId || 'N/A'
        ]);

        autoTable(doc, {
            startY: 40,
            head: [tableColumn],
            body: tableRows,
            theme: 'grid',
            headStyles: { fillColor: [30, 58, 138], textColor: 255, fontStyle: 'bold' },
            styles: { fontSize: 8 },
            columnStyles: {
                4: { fontStyle: 'bold', textColor: [22, 163, 74] } // Green for amount
            }
        });

        doc.save(`PrrepX_Full_Report_${new Date().toLocaleDateString()}.pdf`);
    };

    // --- INDIVIDUAL INVOICE DOWNLOAD ---
    const handleDownloadInvoice = (inv) => {
        setDownloadingInvoice(inv.id);
        const doc = new jsPDF();
        const userName = userMap[inv.userId] || 'N/A';
        
        const generatePDF = (logoBase64) => {
            doc.addImage(logoBase64, 'JPEG', 15, 15, 30, 25);
            doc.setFontSize(22);
            doc.setTextColor(30, 58, 138); 
            doc.text('TAX INVOICE', 195, 25, { align: 'right' });
            
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Invoice No: #INV-${inv.id.substring(0,8).toUpperCase()}`, 195, 32, { align: 'right' });
            doc.text(`Date: ${inv.date?.toDate ? inv.date.toDate().toLocaleDateString() : 'N/A'}`, 195, 37, { align: 'right' });

            // PAID Stamp
            doc.setDrawColor(34, 197, 94);
            doc.setLineWidth(1);
            doc.rect(160, 45, 35, 12);
            doc.setTextColor(34, 197, 94);
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text('PAID', 177.5, 53, { align: 'center' });

            doc.setTextColor(0);
            doc.setFontSize(11);
            doc.text('PrrepX Pathshala Private Limited', 15, 55);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.text(['CIN: U85306UP2025PTC238181', 'GSTIN: 09AAQCP4420J1Z2', 'Lucknow, UP-226301'], 15, 60);

            doc.setFont('helvetica', 'bold');
            doc.text('BILL TO:', 15, 80);
            doc.setFont('helvetica', 'normal');
            doc.text([`Name: ${userName}`, `Email: ${inv.userEmail}`, `User ID: ${inv.userId}`], 15, 85);

            autoTable(doc, {
                startY: 105,
                head: [['Description', 'Qty', 'Unit Price', 'Total']],
                body: [[inv.courseTitle, '1', `INR ${inv.amount}`, `INR ${inv.amount}`]],
                theme: 'striped',
                headStyles: { fillColor: [37, 99, 235], textColor: 255 },
            });

            const finalY = (doc.lastAutoTable) ? doc.lastAutoTable.finalY + 15 : 150;
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(`Total Amount: INR ${inv.amount}`, 195, finalY, { align: 'right' });
            
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.text(`Payment ID: ${inv.paymentId}`, 15, finalY);
            doc.text('This is a computer generated invoice and requires no signature.', 105, 285, { align: 'center' });

            const firstName = userName.split(' ')[0] || 'User';
            doc.save(`invoice_${firstName}_${inv.paymentId}.pdf`);
            setDownloadingInvoice(null);
        };

        const img = new Image();
        img.src = logoImg;
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width; canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            generatePDF(canvas.toDataURL('image/jpeg'));
        };
    };

    if (loading) return <div className={styles.loading}>Loading Dashboard...</div>;

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <div>
                    <h1 className={styles.title}>Test Analytics Dashboard</h1>
                    <p className={styles.subtitle}>Manage student transactions and exports</p>
                </div>
                <div className={styles.actionGroup}>
                    <button onClick={handleExportExcel} className={styles.secondaryBtn}>
                        Excel Export
                    </button>
                    <button onClick={handleExportSummaryPDF} className={styles.secondaryBtn}>
                        PDF Report
                    </button>
                    <select value={dateRange} onChange={e => setDateRange(e.target.value)} className={styles.selectField}>
                        <option value="all">All Time</option>
                        <option value="1m">Last 30 Days</option>
                        <option value="3m">Last 3 Months</option>
                        <option value="6m">Last 6 Months</option>
                        <option value="1y">Last Year</option>
                        <option value="2y">Last 2 Years</option>
                    </select>
                </div>
            </header>

            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <span>Total Revenue</span>
                    <h2>₹{filtered.reduce((sum, inv) => sum + (inv.amount || 0), 0).toLocaleString('en-IN')}</h2>
                </div>
                <div className={styles.statCard}>
                    <span>Invoices</span>
                    <h2>{filtered.length}</h2>
                </div>
                <div className={styles.statCard}>
                    <span>Students</span>
                    <h2>{new Set(filtered.map(i => i.userEmail)).size}</h2>
                </div>
            </div>

            <div className={styles.tableControls}>
                <input 
                    type="text" 
                    placeholder="Search name, email or course..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.searchInput}
                />
                <select
                    value={perPage}
                    onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                    className={styles.selectField}
                >
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                </select>
            </div>

            <div className={styles.tableCard}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Student</th>
                            <th>Description</th>
                            <th>Amount</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginated.length > 0 ? paginated.map(inv => (
                            <tr key={inv.id}>
                                <td>{inv.date?.toDate ? inv.date.toDate().toLocaleDateString() : 'N/A'}</td>
                                <td>
                                    <div className={styles.studentInfo}>
                                        <strong>{userMap[inv.userId] || 'N/A'}</strong>
                                        <span>{inv.userEmail}</span>
                                    </div>
                                </td>
                                <td>{inv.courseTitle}</td>
                                <td className={styles.amountCell}>₹{inv.amount}</td>
                                <td>
                                    <button 
                                        onClick={() => handleDownloadInvoice(inv)} 
                                        className={`${styles.downloadLink} ${downloadingInvoice === inv.id ? styles.downloading : ''}`}
                                        disabled={downloadingInvoice === inv.id}
                                    >
                                        {downloadingInvoice === inv.id ? <span className={styles.spinner}></span> : 'Invoice'}
                                    </button>
                                </td>
                            </tr>
                        )) : (
                            <tr><td colSpan="5" className={styles.noData}>No records found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            {filtered.length > 0 && (
                <div className={styles.pagination}>
                    <span className={styles.paginationInfo}>
                        Showing {filtered.length === 0 ? 0 : (safePage - 1) * perPage + 1}–{Math.min(safePage * perPage, filtered.length)} of {filtered.length} transactions
                    </span>
                    <div className={styles.paginationControls}>
                        <button className={styles.pageBtn} onClick={() => setPage(1)} disabled={safePage === 1}>«</button>
                        <button className={styles.pageBtn} onClick={() => setPage(p => p - 1)} disabled={safePage === 1}>‹ Prev</button>
                        <span className={styles.pageInfo}>Page {safePage} of {totalPages}</span>
                        <button className={styles.pageBtn} onClick={() => setPage(p => p + 1)} disabled={safePage >= totalPages}>Next ›</button>
                        <button className={styles.pageBtn} onClick={() => setPage(totalPages)} disabled={safePage >= totalPages}>»</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminInvoices;